<template>
  <div class="program-collector">
    <!-- Trial: acerto / erro / com ajuda -->
    <div v-if="type === 'trial'" class="d-flex flex-wrap gap-2">
      <button type="button" class="btn btn-success flex-grow-1" :disabled="saving" @click="$emit('record', { trial_result: 'correct' })">
        Acerto
      </button>
      <button type="button" class="btn btn-danger flex-grow-1" :disabled="saving" @click="$emit('record', { trial_result: 'incorrect' })">
        Erro
      </button>
      <button type="button" class="btn btn-warning flex-grow-1" :disabled="saving" @click="$emit('record', { trial_result: 'prompted' })">
        Com ajuda
      </button>
      <button type="button" class="btn btn-light" title="Desfazer último" :disabled="saving || !lastEntryId" @click="$emit('undo')">
        <i class="bi bi-arrow-counterclockwise"></i>
      </button>
    </div>

    <!-- Frequência: contador de ocorrências -->
    <div v-else-if="type === 'frequency'" class="d-flex flex-wrap align-items-center gap-2">
      <button type="button" class="btn btn-primary flex-grow-1 program-collector-count" :disabled="saving" @click="$emit('record', {})">
        <i class="bi bi-plus-lg me-1"></i> Registrar ocorrência
      </button>
      <div class="input-group program-collector-bulk">
        <input v-model="bulkCount" type="number" min="1" max="100" class="form-control form-control-solid" placeholder="Lote">
        <button type="button" class="btn btn-light-primary" :disabled="saving || !bulkCountValid" @click="recordBulk">
          Somar
        </button>
      </div>
      <button type="button" class="btn btn-light" title="Desfazer último" :disabled="saving || !lastEntryId" @click="$emit('undo')">
        <i class="bi bi-arrow-counterclockwise"></i>
      </button>
    </div>

    <!-- Duração e latência: cronômetro -->
    <div v-else-if="type === 'duration' || type === 'latency'">
      <div class="d-flex flex-wrap align-items-center gap-3">
        <div class="program-collector-clock" :class="{ 'is-running': running }">{{ clock }}</div>
        <div class="d-flex flex-wrap gap-2 flex-grow-1">
          <button v-if="!running" type="button" class="btn btn-primary flex-grow-1" :disabled="saving" @click="startTimer">
            <i class="bi bi-play-fill me-1"></i> {{ type === 'latency' ? 'Iniciar (SD)' : 'Iniciar' }}
          </button>
          <button v-else type="button" class="btn btn-success flex-grow-1" :disabled="saving" @click="stopTimer">
            <i class="bi bi-stop-fill me-1"></i> {{ type === 'latency' ? 'Marcar resposta' : 'Parar e registrar' }}
          </button>
          <button v-if="running" type="button" class="btn btn-light" title="Cancelar" @click="resetTimer">
            <i class="bi bi-x-lg"></i>
          </button>
          <button type="button" class="btn btn-light" title="Desfazer último" :disabled="saving || !lastEntryId" @click="$emit('undo')">
            <i class="bi bi-arrow-counterclockwise"></i>
          </button>
        </div>
      </div>

      <div class="input-group input-group-sm mt-3 program-collector-manual">
        <span class="input-group-text">Registrar manualmente</span>
        <input v-model="manualSeconds" type="number" min="1" step="1" class="form-control form-control-solid" placeholder="segundos">
        <button type="button" class="btn btn-light-primary" :disabled="saving || !manualSecondsValid" @click="recordManualSeconds">
          Salvar
        </button>
      </div>

      <div v-if="entryChips.length" class="d-flex flex-wrap gap-2 mt-3">
        <span v-for="chip in entryChips" :key="chip.id" class="badge badge-light-primary">{{ chip.label }}</span>
      </div>
    </div>

    <!-- Percentual: acertos sobre oportunidades -->
    <div v-else-if="type === 'percentage'">
      <div class="row g-2 align-items-end">
        <div class="col-4 col-md-3">
          <label class="form-label fs-8 text-muted mb-1">Acertos</label>
          <input v-model="hits" type="number" min="0" class="form-control form-control-solid" placeholder="0">
        </div>
        <div class="col-4 col-md-3">
          <label class="form-label fs-8 text-muted mb-1">Oportunidades</label>
          <input v-model="opportunities" type="number" min="1" class="form-control form-control-solid" placeholder="10">
        </div>
        <div class="col-4 col-md-3">
          <label class="form-label fs-8 text-muted mb-1">Resultado</label>
          <div class="form-control form-control-solid bg-light-primary fw-bold">{{ previewPercent }}</div>
        </div>
        <div class="col-12 col-md-3 d-flex gap-2">
          <button type="button" class="btn btn-primary flex-grow-1" :disabled="saving || !percentageValid" @click="recordPercentage">
            Registrar
          </button>
          <button type="button" class="btn btn-light" title="Desfazer último" :disabled="saving || !lastEntryId" @click="$emit('undo')">
            <i class="bi bi-arrow-counterclockwise"></i>
          </button>
        </div>
      </div>

      <div v-if="entryChips.length" class="d-flex flex-wrap gap-2 mt-3">
        <span v-for="chip in entryChips" :key="chip.id" class="badge badge-light-primary">{{ chip.label }}</span>
      </div>
    </div>

    <!-- Intervalo: marcação a cada intervalo cronometrado -->
    <div v-else-if="type === 'interval'">
      <div class="d-flex flex-wrap align-items-center gap-3 mb-3">
        <div class="program-collector-clock" :class="{ 'is-running': intervalRunning, 'is-due': intervalDue }">
          {{ intervalClock }}
        </div>
        <div class="d-flex flex-column">
          <span class="fs-8 text-muted">{{ intervalTypeLabelText }} · {{ intervalSeconds }}s por intervalo</span>
          <span class="fs-8" :class="intervalDue ? 'text-danger fw-bold' : 'text-muted'">
            {{ intervalDue ? 'Marque o intervalo' : (intervalRunning ? 'Observando...' : 'Cronômetro parado') }}
          </span>
        </div>
        <div class="ms-auto d-flex gap-2">
          <button v-if="!intervalRunning" type="button" class="btn btn-sm btn-light-primary" @click="startIntervalTimer">
            <i class="bi bi-play-fill"></i> {{ intervalDue ? 'Retomar' : 'Iniciar cronômetro' }}
          </button>
          <button v-else type="button" class="btn btn-sm btn-light" @click="pauseIntervalTimer">
            <i class="bi bi-pause-fill"></i> Pausar
          </button>
        </div>
      </div>

      <div class="d-flex flex-wrap gap-2">
        <button type="button" class="btn btn-success flex-grow-1" :disabled="saving" @click="markInterval(true)">
          Ocorreu
        </button>
        <button type="button" class="btn btn-light-danger flex-grow-1" :disabled="saving" @click="markInterval(false)">
          Não ocorreu
        </button>
        <button type="button" class="btn btn-light" title="Desfazer último" :disabled="saving || !lastEntryId" @click="$emit('undo')">
          <i class="bi bi-arrow-counterclockwise"></i>
        </button>
      </div>

      <div v-if="intervalMarks.length" class="d-flex flex-wrap gap-1 mt-3">
        <span
          v-for="mark in intervalMarks"
          :key="mark.id"
          class="program-collector-mark"
          :class="mark.occurred ? 'is-yes' : 'is-no'"
          :title="mark.occurred ? 'Ocorreu' : 'Não ocorreu'"></span>
      </div>
    </div>

    <!-- Manual: anotação livre -->
    <div v-else-if="type === 'manual'">
      <textarea
        v-model="note"
        class="form-control form-control-solid"
        rows="2"
        maxlength="2000"
        placeholder="Descreva a observação desta coleta..."></textarea>
      <div class="d-flex flex-wrap gap-2 mt-2">
        <button type="button" class="btn btn-primary" :disabled="saving || !note.trim()" @click="recordNote">
          Salvar anotação
        </button>
        <button type="button" class="btn btn-light" title="Desfazer último" :disabled="saving || !lastEntryId" @click="$emit('undo')">
          <i class="bi bi-arrow-counterclockwise"></i>
        </button>
      </div>

      <div v-if="notes.length" class="program-collector-notes mt-3">
        <div v-for="item in notes" :key="item.id_patient_program_entry" class="program-collector-note">
          <div class="fs-8 text-muted">{{ formatTime(item.created_at) }}</div>
          <div class="fs-7">{{ item.value_text }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import {
  DEFAULT_INTERVAL_SECONDS,
  clockFormat,
  formatSeconds,
  intervalTypeLabel,
} from '../../../helpers/programMeasurement';

export default {
  name: 'ProgramCollector',
  props: {
    program: { type: Object, required: true },
    saving: { type: Boolean, default: false },
  },
  emits: ['record', 'undo'],
  data() {
    return {
      running: false,
      elapsed: 0,
      startedAt: null,
      timerId: null,
      manualSeconds: '',
      bulkCount: '',
      hits: '',
      opportunities: '',
      note: '',
      intervalRunning: false,
      intervalDue: false,
      intervalRemaining: 0,
      intervalTimerId: null,
    };
  },
  computed: {
    type() {
      return this.program.measurement_type || 'trial';
    },
    settings() {
      return this.program.program_settings || {};
    },
    lastEntryId() {
      return this.program.session?.last_entry_id || null;
    },
    entries() {
      return this.program.entries || [];
    },
    clock() {
      return clockFormat(this.elapsed);
    },
    manualSecondsValid() {
      const value = Number(this.manualSeconds);
      return Number.isFinite(value) && value > 0;
    },
    bulkCountValid() {
      const value = Number(this.bulkCount);
      return Number.isInteger(value) && value >= 1 && value <= 100;
    },
    percentageValid() {
      if (this.hits === '' || this.hits === null || this.opportunities === '' || this.opportunities === null) return false;
      const hits = Number(this.hits);
      const opportunities = Number(this.opportunities);
      return Number.isInteger(hits) && Number.isInteger(opportunities)
        && opportunities >= 1 && hits >= 0 && hits <= opportunities;
    },
    previewPercent() {
      if (!this.percentageValid) return '—';
      return `${Math.round((Number(this.hits) / Number(this.opportunities)) * 1000) / 10}%`;
    },
    entryChips() {
      return this.entries.slice(0, 12).map((entry) => ({
        id: entry.id_patient_program_entry,
        label: this.type === 'percentage'
          ? `${entry.value_numerator}/${entry.value_denominator}`
          : formatSeconds(entry.value_numeric),
      }));
    },
    intervalMarks() {
      return this.entries.slice(0, 40).map((entry) => ({
        id: entry.id_patient_program_entry,
        occurred: !!entry.value_boolean,
      })).reverse();
    },
    notes() {
      return this.entries.filter((entry) => !!entry.value_text).slice(0, 10);
    },
    intervalSeconds() {
      const seconds = Number(this.settings.interval_seconds);
      return Number.isFinite(seconds) && seconds > 0 ? seconds : DEFAULT_INTERVAL_SECONDS;
    },
    intervalTypeLabelText() {
      return intervalTypeLabel(this.settings.interval_type);
    },
    intervalClock() {
      return clockFormat(this.intervalRemaining);
    },
  },
  watch: {
    intervalSeconds(seconds) {
      if (!this.intervalRunning) this.intervalRemaining = seconds;
    },
  },
  mounted() {
    this.opportunities = this.settings.opportunities || 10;
    this.intervalRemaining = this.intervalSeconds;
  },
  beforeUnmount() {
    this.clearTimers();
  },
  methods: {
    formatTime(value) {
      return value ? moment(value).format('HH:mm') : '';
    },
    clearTimers() {
      if (this.timerId) clearInterval(this.timerId);
      if (this.intervalTimerId) clearInterval(this.intervalTimerId);
      this.timerId = null;
      this.intervalTimerId = null;
    },
    startTimer() {
      this.running = true;
      this.startedAt = Date.now();
      this.elapsed = 0;
      this.timerId = setInterval(() => {
        this.elapsed = (Date.now() - this.startedAt) / 1000;
      }, 200);
    },
    stopTimer() {
      const seconds = Math.max(0.1, Math.round(((Date.now() - this.startedAt) / 1000) * 100) / 100);
      this.resetTimer();
      this.$emit('record', { value_numeric: seconds });
    },
    resetTimer() {
      if (this.timerId) clearInterval(this.timerId);
      this.timerId = null;
      this.running = false;
      this.elapsed = 0;
      this.startedAt = null;
    },
    recordManualSeconds() {
      if (!this.manualSecondsValid) return;
      this.$emit('record', { value_numeric: Number(this.manualSeconds) });
      this.manualSeconds = '';
    },
    recordBulk() {
      if (!this.bulkCountValid) return;
      this.$emit('record', { value_numeric: Number(this.bulkCount) });
      this.bulkCount = '';
    },
    recordPercentage() {
      if (!this.percentageValid) return;
      this.$emit('record', {
        value_numerator: Number(this.hits),
        value_denominator: Number(this.opportunities),
      });
      this.hits = '';
    },
    recordNote() {
      const text = this.note.trim();
      if (!text) return;
      this.$emit('record', { value_text: text });
      this.note = '';
    },
    startIntervalTimer() {
      this.intervalDue = false;
      this.intervalRunning = true;
      if (!this.intervalRemaining) this.intervalRemaining = this.intervalSeconds;
      if (this.intervalTimerId) clearInterval(this.intervalTimerId);
      this.intervalTimerId = setInterval(() => {
        this.intervalRemaining -= 1;
        if (this.intervalRemaining <= 0) {
          this.intervalRemaining = 0;
          this.intervalRunning = false;
          this.intervalDue = true;
          clearInterval(this.intervalTimerId);
          this.intervalTimerId = null;
          this.beep();
        }
      }, 1000);
    },
    pauseIntervalTimer() {
      if (this.intervalTimerId) clearInterval(this.intervalTimerId);
      this.intervalTimerId = null;
      this.intervalRunning = false;
    },
    markInterval(occurred) {
      this.$emit('record', { value_boolean: occurred });
      if (this.intervalDue || this.intervalRunning) {
        this.intervalRemaining = this.intervalSeconds;
        this.startIntervalTimer();
      }
    },
    beep() {
      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;
        const context = new AudioContextClass();
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.frequency.value = 880;
        gain.gain.value = 0.05;
        oscillator.connect(gain).connect(context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + 0.15);
        oscillator.onended = () => context.close();
      } catch (e) {
        // Áudio é opcional: o aviso visual já indica o fim do intervalo.
      }
    },
  },
};
</script>

<style scoped>
.program-collector-clock {
  font-size: 1.6rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #181c32;
  min-width: 86px;
}
.program-collector-clock.is-running {
  color: #009ef7;
}
.program-collector-clock.is-due {
  color: #f1416c;
}
.program-collector-bulk {
  max-width: 190px;
}
.program-collector-manual {
  max-width: 420px;
}
.program-collector-mark {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  display: inline-block;
}
.program-collector-mark.is-yes {
  background: #50cd89;
}
.program-collector-mark.is-no {
  background: #e4e6ef;
}
.program-collector-notes {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 190px;
  overflow: auto;
}
.program-collector-note {
  border-left: 3px solid #e4e6ef;
  padding-left: 10px;
}
</style>
